import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TOONS } from '@/data/content';
import ToonCard from '@/components/ToonCard';
import ToonModal from '@/components/ToonModal';

export default function ToonGallery() {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [bursts, setBursts] = useState([]);

  const onSelect = (toon, e) => {
    setSelected(toon);
    const burst = {
      id: Date.now(),
      x: e.clientX,
      y: e.clientY,
      color: toon.accent
    };
    setBursts((b) => [...b, burst]);
    setTimeout(() => setBursts((b) => b.filter((it) => it.id !== burst.id)), 900);
  };

  return (
    <section
      id="toons"
      data-testid="toons-section"
      className="relative overflow-hidden py-24 sm:py-32"
      aria-label="Meet the Toons"
    >
      <motion.div
        aria-hidden="true"
        data-testid="toon-mood-overlay"
        className="pointer-events-none fixed inset-0 z-[3]"
        animate={{
          background: hovered
            ? `radial-gradient(60% 55% at 50% 45%, ${hovered.accentSoft.replace('0.16', '0.22')} 0%, transparent 70%)`
            : 'radial-gradient(60% 55% at 50% 45%, rgba(0,200,255,0.06) 0%, transparent 70%)'
        }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">02 — THE TOONS</p>
          <h2
            data-testid="toons-title"
            className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl"
          >
            MEET THE{' '}
            <span className="bg-gradient-to-r from-gold to-ember bg-clip-text text-transparent text-glow-gold">TOONS</span>
          </h2>
          <p className="mt-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-snow/60" data-testid="toons-subtitle">
            EVERY TOON HAS A STORY.
          </p>
        </motion.div>
      </div>

      <div className="relative z-10 mt-12">
        <div
          className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-10 sm:gap-6 sm:px-10 lg:px-16"
          data-testid="toon-gallery-track"
        >
          {TOONS.map((toon) => (
            <ToonCard
              key={toon.id}
              toon={toon}
              onHover={setHovered}
              onLeave={() => setHovered(null)}
              onSelect={onSelect}
            />
          ))}
          <div className="flex min-w-[200px] snap-center items-center justify-center rounded-3xl border border-dashed border-electric/25 p-8" data-testid="toon-more-slot">
            <p className="text-center font-mono text-[10px] uppercase leading-loose tracking-[0.3em] text-snow/40">
              MORE TOONS
              <br />
              WILL APPEAR
              <br />
              IN THE CITY...
            </p>
          </div>
        </div>
        <p className="mt-2 px-6 text-center font-mono text-[9px] uppercase tracking-[0.35em] text-snow/35 sm:px-10">
          SCROLL SIDEWAYS — CLICK A TOON TO OPEN ITS FILE
        </p>
      </div>

      {bursts.map((b) => (
        <div key={b.id} className="pointer-events-none fixed left-0 top-0 z-[290]" aria-hidden="true">
          {[...Array(14)].map((_, i) => {
            const angle = (i / 14) * Math.PI * 2;
            const dist = 50 + Math.random() * 70;
            return (
              <motion.span
                key={i}
                className="absolute rounded-full"
                style={{ left: b.x, top: b.y, width: 4 + (i % 3) * 2, height: 4 + (i % 3) * 2, backgroundColor: b.color }}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, opacity: 0, scale: 0.2 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            );
          })}
        </div>
      ))}

      <AnimatePresence>
        {selected && <ToonModal toon={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
